/**
 * A `.zip` of files, stored without compression — for downloading several
 * documents as one file instead of a browser prompt per file.
 *
 * Stored, not deflated: the files are PDFs, spreadsheets and images, which are
 * compressed already, so deflating them again would cost time and save almost
 * nothing — and it keeps this a few dozen lines with no dependency. The format
 * is PKWARE's APPNOTE: a local header before each file, then a central
 * directory, then its end record. Names are UTF-8 (flag bit 11).
 *
 * The real app should build large bundles on the server (or stream them), so
 * the browser is not holding a whole batch in memory.
 */

const CRC_TABLE = (() => {
  const table = new Uint32Array(256)
  for (let n = 0; n < 256; n += 1) {
    let c = n
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    table[n] = c >>> 0
  }
  return table
})()

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff
  for (let i = 0; i < bytes.length; i += 1) crc = CRC_TABLE[(crc ^ bytes[i]!) & 0xff]! ^ (crc >>> 8)
  return (crc ^ 0xffffffff) >>> 0
}

/** DOS date and time, which is what a zip records. */
function dosTime(d: Date): { time: number; date: number } {
  return {
    time: (d.getHours() << 11) | (d.getMinutes() << 5) | Math.floor(d.getSeconds() / 2),
    date: ((d.getFullYear() - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate(),
  }
}

/** Two files may not share a name inside one zip: "a.pdf", "a (2).pdf". */
function uniqueNames(names: string[]): string[] {
  const seen = new Map<string, number>()
  return names.map((name) => {
    const n = (seen.get(name.toLowerCase()) ?? 0) + 1
    seen.set(name.toLowerCase(), n)
    if (n === 1) return name
    const dot = name.lastIndexOf('.')
    return dot > 0 ? `${name.slice(0, dot)} (${n})${name.slice(dot)}` : `${name} (${n})`
  })
}

export async function zipFiles(files: { name: string; blob: Blob }[]): Promise<Blob> {
  const encoder = new TextEncoder()
  const { time, date } = dosTime(new Date())
  const names = uniqueNames(files.map((f) => f.name))
  const parts: BlobPart[] = []
  const central: Uint8Array<ArrayBuffer>[] = []
  let offset = 0

  for (let i = 0; i < files.length; i += 1) {
    const data = new Uint8Array(await files[i]!.blob.arrayBuffer())
    const name = new Uint8Array(encoder.encode(names[i]!))
    const crc = crc32(data)

    const local = new DataView(new ArrayBuffer(30))
    local.setUint32(0, 0x04034b50, true) // local file header
    local.setUint16(4, 20, true) // version needed
    local.setUint16(6, 0x0800, true) // UTF-8 names
    local.setUint16(8, 0, true) // stored
    local.setUint16(10, time, true)
    local.setUint16(12, date, true)
    local.setUint32(14, crc, true)
    local.setUint32(18, data.length, true)
    local.setUint32(22, data.length, true)
    local.setUint16(26, name.length, true)
    local.setUint16(28, 0, true)
    parts.push(local.buffer, name, data)

    const entry = new DataView(new ArrayBuffer(46))
    entry.setUint32(0, 0x02014b50, true) // central directory header
    entry.setUint16(4, 20, true) // made by
    entry.setUint16(6, 20, true) // needed
    entry.setUint16(8, 0x0800, true)
    entry.setUint16(10, 0, true)
    entry.setUint16(12, time, true)
    entry.setUint16(14, date, true)
    entry.setUint32(16, crc, true)
    entry.setUint32(20, data.length, true)
    entry.setUint32(24, data.length, true)
    entry.setUint16(28, name.length, true)
    entry.setUint32(42, offset, true) // where its local header is
    const record = new Uint8Array(46 + name.length)
    record.set(new Uint8Array(entry.buffer), 0)
    record.set(name, 46)
    central.push(record)

    offset += 30 + name.length + data.length
  }

  const size = central.reduce((n, c) => n + c.length, 0)
  const end = new DataView(new ArrayBuffer(22))
  end.setUint32(0, 0x06054b50, true) // end of central directory
  end.setUint16(8, files.length, true)
  end.setUint16(10, files.length, true)
  end.setUint32(12, size, true)
  end.setUint32(16, offset, true)

  return new Blob([...parts, ...central, end.buffer], { type: 'application/zip' })
}

/** Hand a file to the browser to save. */
export function saveFile(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  // After the click has been handled, not before.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
